import { GlobalStore } from '@roc/feature-app-core';
import { generateUUID, PURCHASE, REFINANCE } from '@roc/feature-utils';
import { action, makeObservable, observable } from 'mobx';
import { PROPERTY_TYPE_OTHER, PropertySource } from '../../utils/constants';
import { PropertyFormStore } from './propertyFormStore';
import { IPropertiesStore } from '../common/iQuoteTypeStore';

const REQUIRED_MESSAGE = 'This field is required';

const propertyInformationForm = {
  fields: {
    propertyId: {
      value: '',
      error: null,
      rule: '',
      message: '',
    },
    address: {
      value: '',
      error: null,
      rule: 'required',
      message: 'The address is required.',
    },
    __isAddressInCorrectFormat__: {
      value: true,
      error: null,
      rule: 'accepted',
      message: 'Invalid Address Format.',
    },
    streetNumber: {
      value: '',
      error: null,
      rule: '',
    },
    streetName: {
      value: '',
      error: null,
      //rule: 'required',
      message: 'The Street Name is required.',
    },
    city: {
      value: '',
      error: null,
      rule: 'required',
      message: 'The City is required.',
    },
    state: {
      value: '',
      error: null,
      rule: 'required',
      message: 'The State is required.',
    },
    county: {
      value: '',
      error: null,
      rule: '',
    },
    zipCode: {
      value: '',
      error: null,
      rule: 'required',
      message: 'The Zip Code is required.',
    },
    latitude: {
      value: 0,
      error: null,
      rule: '',
    },
    longitude: {
      value: 0,
      error: null,
      rule: '',
    },
    aptNumber: {
      value: '',
      error: null,
      rule: '',
    },
    propertyType: {
      value: '',
      error: null,
      rule: 'required',
      message: 'The property type is required.',
    },
    propertyTypeDescription: {
      value: '',
      error: null,
      rule: [{ required_if: ['propertyType', PROPERTY_TYPE_OTHER] }],
      message: 'This field is required.',
    },
    units: {
      value: '',
      error: null,
      rule: '',
      message: 'This field is required.',
    },
    loanPurpose: {
      value: '',
      error: null,
      rule: 'required',
      message: REQUIRED_MESSAGE,
    },
    purchaseDate: {
      type: 'DATE',
      value: null,
      error: null,
      rule: [
        {
          required_if_one_of: ['loanPurpose', REFINANCE],
        },
        { valid_date: true },
      ],
      format: 'MM/dd/yyyy',
      message: 'Purchase Date is required.',
    },
    propertySourcing: {
      value: '',
      error: null,
      rule: [
        {
          required_if_one_of: ['loanPurpose', PURCHASE],
        },
      ],
      message: REQUIRED_MESSAGE,
    },
    sellerCredit: {
      value: '',
      error: null,
      rule: [
        {
          required_if_one_of: ['loanPurpose', PURCHASE],
        },
      ],
      message: REQUIRED_MESSAGE,
    },
    sellerCreditAmount: {
      value: '',
      error: null,
      rule: [
        {
          required_if_one_of: ['sellerCredit', true],
        },
      ],
      message: 'The Seller Credit Amount is required.',
    },
    borrowerKnowOwnerOfRecord: {
      value: '',
      error: null,
      rule: [
        {
          required_if_one_of: ['loanPurpose', PURCHASE],
        },
      ],
      message: REQUIRED_MESSAGE,
    },
    armsLengthComment: {
      value: '',
      error: null,
      rule: [
        {
          required_if: ['borrowerKnowOwnerOfRecord', true],
        },
      ],
      message: REQUIRED_MESSAGE,
    },
    outstandingLoanOnTheProperty: {
      value: '',
      error: null,
      rule: [{ required_if_one_of: ['loanPurpose', REFINANCE] }],
      message: REQUIRED_MESSAGE,
    },
    payoffAmount: {
      value: '',
      error: null,
      rule: [
        {
          required_if_one_of: ['outstandingLoanOnTheProperty', true],
        },
      ],
      message: REQUIRED_MESSAGE,
    },
    loanEverBeenInDefault: {
      value: '',
      error: null,
      rule: [
        {
          required_if_one_of: ['outstandingLoanOnTheProperty', true],
        },
      ],
      message: REQUIRED_MESSAGE,
    },
    purchasePrice: {
      value: '',
      error: null,
      rule: 'required',
      message: REQUIRED_MESSAGE,
    },
    currentMarketValue: {
      value: '',
      error: null,
      rule: [{ required_if_one_of: ['loanPurpose', REFINANCE] }],
      message: REQUIRED_MESSAGE,
    },
    constructionRenoSpendToDate: {
      value: '',
      error: null,
      rule: [{ required_if_one_of: ['loanPurpose', REFINANCE] }],
      message: REQUIRED_MESSAGE,
    },
    renovationBudget: {
      value: '',
      error: null,
      rule: 'required',
      message: REQUIRED_MESSAGE,
    },
    marketValueAfterCompletion: {
      value: '',
      error: null,
      rule: 'required',
      message: 'The Market Value After Completion is required.',
    },
    squareFootageExpansion: {
      value: '',
      error: null,
      rule: '',
      message: REQUIRED_MESSAGE,
    },
    permitsInPlace: {
      value: false,
      error: null,
      rule: '',
      message: REQUIRED_MESSAGE,
    },
    wholesalerPropertyContractAmount: {
      value: '',
      error: null,
      rule: [
        { required_if_one_of: ['propertySourcing', PropertySource.WHOLESALER] },
      ],
      message: REQUIRED_MESSAGE,
    },
    propertySourcingExplanation: {
      value: '',
      error: null,
      rule: [
        { required_if_one_of: ['propertySourcing', PropertySource.OTHER] },
      ],
      message: REQUIRED_MESSAGE,
    },
    //Supplemental Information
    condoConversion: {
      value: '',
      error: null,
    },
    fireDamage: {
      value: '',
      error: null,
    },
    hasNewAddress: {
      value: '',
      error: null,
      rule: '',
      message: REQUIRED_MESSAGE,
    },
    exitStrategy: {
      value: '',
      error: null,
      rule: [{ required_if_true: 'isPreflight' }],
      message: REQUIRED_MESSAGE,
    },
    newAddress: {
      value: null,
      error: null,
      rule: [{ required_if_true: ['isPreflight', 'hasNewAddress'] }],
      message: REQUIRED_MESSAGE,
    },
    monthlyMarketRentProvidedAtOrigination: {
      value: '',
      error: null,
      attribute: 'Monthly Market Rent',
    },
    annualHOAProvidedAtOrigination: {
      value: '',
      error: null,
      attribute: 'Annual HOA Dues',
    },
    annualTaxesProvidedAtOrigination: {
      value: '',
      error: null,
      attribute: 'Annual Taxes',
    },
    annualInsuranceProvidedAtOrigination: {
      value: '',
      error: null,
      attribute: 'Annual Insurance',
    },
    //Flags
    isAppraisalAvailable: { value: false },
    isPreflight: { value: false },
    //Term Sheet Fields
    releaseAmount: { value: null },
    propertyLoanAllocatedAmount: { value: null },
    describeRenovation: {
      value: '',
      error: null,
      rule: 'required',
      message: REQUIRED_MESSAGE,
    },
    medianZipCodeValue: {
      value: '',
      error: null,
    },
  },
  meta: {
    key: null,
    editMode: false,
    isValid: false,
    error: null,
  },
};

export class FixFlipPropertiesStore implements IPropertiesStore {
  protected globalStore: GlobalStore;
  protected loanSubType: string;

  public propertyFormStores: PropertyFormStore[];
  public key = generateUUID();

  constructor(globalStore, loanSubType) {
    this.globalStore = globalStore;
    this.loanSubType = loanSubType;
    makeObservable(this, {
      reset: action,
      propertyFormStores: observable,
      addNewProperty: action,
      removeProperty: action,
      validateAllProperties: action,
      turnOffPropertiesEditMode: action,
      getProperties: action,
      loadProperties: action,
    });
  }

  private createPropertyForm() {
    return new PropertyFormStore(
      propertyInformationForm,
      this.globalStore,
      this.loanSubType
    );
  }

  reset() {
    this.propertyFormStores = [];
    this.addNewProperty();
  }

  addNewProperty() {
    this.propertyFormStores.push(this.createPropertyForm());
  }

  removeProperty(propertyKey: string) {
    this.propertyFormStores = this.propertyFormStores.filter(
      property => property.key !== propertyKey
    );
  }

  validateAllProperties() {
    let isValid = true;

    this.propertyFormStores.forEach(store => {
      const state = store.form.fields.state.value;

      store.validateAndCleanupProperty();
      isValid = isValid && store.form.meta.isValid;
      store.setEditMode(!isValid);
    });

    if (!isValid) {
      this.globalStore.notificationStore.showWarningNotification({
        message: 'Please complete all the required fields for the properties.',
      });
      return false;
    }

    return true;
  }

  turnOffPropertiesEditMode() {
    this.propertyFormStores.forEach(propertyFormStore => {
      propertyFormStore.form.meta.editMode = false;
    });
  }

  getProperties() {
    return this.propertyFormStores
      .map(form => form.getProperty())
      .filter(property => !!property.loanPurpose);
  }

  loadProperties(quoteData) {
    const properties = quoteData.properties;
    if (properties?.length > 0) {
      this.propertyFormStores = properties?.map(property => {
        const form = this.createPropertyForm();
        form.loadProperty(property);
        form.setEditMode(false);
        return form;
      });
    }
  }
}
